import { useEffect, useState } from "react";
import { Loader2, ArrowLeft, Trash2, Download, Mail, Phone, MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATUSES = ["NEW", "REVIEWING", "SHORTLISTED", "INTERVIEW", "SELECTED", "REJECTED"];

const STATUS_STYLES = {
  NEW: "bg-cts-soft text-cts-charcoal",
  REVIEWING: "bg-blue-100 text-blue-800",
  SHORTLISTED: "bg-amber-100 text-amber-800",
  INTERVIEW: "bg-purple-100 text-purple-800",
  SELECTED: "bg-green-100 text-green-800",
  REJECTED: "bg-cts-redlight text-cts-red",
};

const ApplicationDetail = ({ id, onBack, onChanged }) => {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/job-applications/${id}`).then(({ data }) => setApp(data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const changeStatus = async (status) => {
    setError("");
    try {
      const { data } = await api.patch(`/admin/job-applications/${id}`, { status });
      setApp(data);
      onChanged();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not update status");
    }
  };

  const downloadCv = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/job-applications/${id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = app.cv_filename || "cv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not download CV");
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !app) {
    return <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-cts-red" size={24} /></div>;
  }

  return (
    <div className="rounded-xl border border-cts-line bg-white p-6 space-y-5" data-testid="application-detail">
      <button onClick={onBack} data-testid="application-back-btn" className="flex items-center gap-1.5 text-sm font-semibold text-cts-charcoal hover:text-cts-red">
        <ArrowLeft size={15} /> Back to Applications
      </button>
      {error && <p className="rounded-md bg-cts-redlight px-4 py-3 text-sm text-cts-ink" role="alert">{error}</p>}
      <div>
        <p className="eyebrow mb-1">Applying for</p>
        <h2 className="font-heading font-bold text-cts-ink text-xl">{app.position}</h2>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 text-sm">
        <div><span className="text-cts-charcoal/60">Full Name</span><p className="font-semibold text-cts-ink">{app.full_name}</p></div>
        <div><span className="text-cts-charcoal/60 flex items-center gap-1"><Phone size={12} /> Phone</span><p className="font-semibold text-cts-ink">{app.phone}</p></div>
        <div><span className="text-cts-charcoal/60 flex items-center gap-1"><Mail size={12} /> Email</span><p className="font-semibold text-cts-ink">{app.email}</p></div>
        {app.location && <div><span className="text-cts-charcoal/60 flex items-center gap-1"><MapPin size={12} /> Location</span><p className="font-semibold text-cts-ink">{app.location}</p></div>}
        {app.qualification && <div><span className="text-cts-charcoal/60">Qualification</span><p className="font-semibold text-cts-ink">{app.qualification}</p></div>}
        {app.experience && <div><span className="text-cts-charcoal/60">Experience</span><p className="font-semibold text-cts-ink">{app.experience}</p></div>}
        {app.current_previous_role && <div><span className="text-cts-charcoal/60">Current / Previous Role</span><p className="font-semibold text-cts-ink">{app.current_previous_role}</p></div>}
        <div><span className="text-cts-charcoal/60">Applied On</span><p className="font-semibold text-cts-ink">{new Date(app.created_at).toLocaleString("en-IN")}</p></div>
      </div>
      {app.cover_message && (
        <div>
          <p className="text-xs font-heading font-bold uppercase tracking-wide text-cts-ink mb-1">Cover Message</p>
          <p className="text-sm text-cts-charcoal whitespace-pre-line">{app.cover_message}</p>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-4">
        <button onClick={downloadCv} disabled={downloading} data-testid="application-download-cv-btn" className="btn-outline">
          {downloading ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />} DOWNLOAD CV
        </button>
        <div>
          <label className="field-label !mb-1">Status</label>
          <select data-testid="application-status-select" className="field-input !w-auto" value={app.status} onChange={(e) => changeStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
};

export const JobApplicationsTab = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/job-applications");
      setApplications(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete the application from "${name}"? This cannot be undone.`)) return;
    await api.delete(`/admin/job-applications/${id}`);
    load();
  };

  if (viewing) {
    return <ApplicationDetail id={viewing} onBack={() => { setViewing(null); load(); }} onChanged={load} />;
  }

  return (
    <div data-testid="admin-job-applications-tab">
      {loading ? (
        <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-cts-red" size={24} /></div>
      ) : applications.length === 0 ? (
        <div className="rounded-xl border border-cts-line bg-white p-14 text-center" data-testid="admin-applications-empty">
          <p className="font-heading font-bold text-cts-ink">No applications yet</p>
          <p className="text-sm text-cts-charcoal mt-1">Job applications submitted by candidates will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-cts-line bg-white" data-testid="admin-applications-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cts-line text-left">
                {["Applicant", "Position", "Date", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-heading font-bold text-xs uppercase tracking-wide text-cts-charcoal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {applications.map((a) => (
                <tr key={a.id} className="border-b border-cts-line last:border-0 cursor-pointer hover:bg-cts-soft" data-testid={`application-row-${a.id}`} onClick={() => setViewing(a.id)}>
                  <td className="px-4 py-3 font-semibold text-cts-ink">{a.full_name}</td>
                  <td className="px-4 py-3 text-cts-charcoal">{a.position}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-cts-charcoal">{new Date(a.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  <td className="px-4 py-3"><span className={`rounded px-2 py-1 text-xs font-bold ${STATUS_STYLES[a.status]}`}>{a.status}</span></td>
                  <td className="px-4 py-3">
                    <button onClick={(e) => { e.stopPropagation(); onDelete(a.id, a.full_name); }} data-testid={`application-delete-${a.id}`} className="text-cts-charcoal/60 hover:text-cts-red" aria-label={`Delete application from ${a.full_name}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
